import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsPersonVcard, BsTelephone, BsGlobe } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';

import { createContact } from '../../../api/crmApi';
import { useVisibleFieldDefs, useCustomFieldDefs } from '../../../models/customFields/queries';
import CustomFieldsSection from '../../common/CustomFieldsSection/CustomFieldsSection';

const SectionLabel = ({ icon: Icon, children }) => (
	<div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-bgBase-800 mb-2">
		<Icon className="w-3 h-3" /> {children}
	</div>
);

SectionLabel.propTypes = {
	icon: PropTypes.elementType.isRequired,
	children: PropTypes.node.isRequired,
};

const FieldLabel = ({ label, required }) => (
	<label className="block text-[11px] font-medium text-bgBase-800 mb-1">
		{label}
		{required && <span className="text-warning-500 ml-0.5">*</span>}
	</label>
);

FieldLabel.propTypes = {
	label: PropTypes.string.isRequired,
	required: PropTypes.bool,
};

const NewContactModal = ({ isOpen, onClose, companyId, onCreated }) => {
	const [fields, setFields] = useState({
		salutation: '',
		firstName: '',
		lastName: '',
		emailAddress: '',
		phoneNumber: '',
		companyRole: '',
		linkedinUrl: '',
	});
	const [customFields, setCustomFields] = useState({});
	const [isPrimary, setIsPrimary] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const showError = useErrorStore((s) => s.showError);

	const { data: visibleDefs = [] } = useVisibleFieldDefs('contact');
	const { data: allDefs = [] } = useCustomFieldDefs('contact');

	// Which default fields are visible and required per org config
	const visible = useMemo(() => {
		const set = new Set();
		for (const def of visibleDefs) {
			if (def.isDefault) set.add(def.fieldKey);
		}
		return set;
	}, [visibleDefs]);

	const requiredKeys = useMemo(() => {
		const set = new Set();
		for (const def of visibleDefs) {
			if (def.isDefault && def.isRequired) set.add(def.fieldKey);
		}
		return set;
	}, [visibleDefs]);

	const hasCustomFields = allDefs.some((f) => !f.isDefault);

	const hasRequiredFieldsMissing = useMemo(() => {
		for (const key of requiredKeys) {
			const val = fields[key];
			if (!val || (typeof val === 'string' && !val.trim())) return true;
		}
		return allDefs.some((def) => {
			if (def.isDefault || !def.isRequired) return false;
			const val = customFields[def.id];
			return val === undefined || val === null || val === '';
		});
	}, [requiredKeys, fields, allDefs, customFields]);

	const hasMinInput = fields.firstName?.trim() || fields.lastName?.trim() || fields.emailAddress?.trim();

	const set = (key) => (e) => setFields((prev) => ({ ...prev, [key]: e.target.value }));
	const isReq = (key) => requiredKeys.has(key);

	// Salutation options from the field def
	const salutationDef = visibleDefs.find((d) => d.isDefault && d.fieldKey === 'salutation');
	const salutationOptions = salutationDef?.selectOptions ?? ['Herr', 'Frau', 'Divers'];

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!hasMinInput || hasRequiredFieldsMissing) return;
		setSubmitting(true);
		try {
			const cf = Object.fromEntries(Object.entries(customFields).filter(([, v]) => v !== '' && v !== undefined));
			await createContact({
				crmCompanyId: companyId,
				firstName: fields.firstName?.trim() || null,
				lastName: fields.lastName?.trim() || null,
				companyRole: fields.companyRole?.trim() || null,
				emailAddress: fields.emailAddress?.trim() || null,
				phoneNumber: fields.phoneNumber?.trim() || null,
				salutation: fields.salutation || null,
				linkedinUrl: fields.linkedinUrl?.trim() || null,
				isPrimary,
				...(Object.keys(cf).length > 0 ? { customFields: cf } : {}),
			});
			onCreated?.();
			onClose();
		} catch (err) {
			showError({ error: err, title: 'Kontakt konnte nicht erstellt werden' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={handleSubmit} size="m">
			<Panel.Header onClose={onClose} subtitle="Erstellen Sie einen neuen Kontakt">
				Neuer Kontakt
			</Panel.Header>
			<Panel.Body>
				<div className="mb-5">
					<SectionLabel icon={BsPersonVcard}>Person</SectionLabel>
					{visible.has('salutation') && (
						<div className="mb-3">
							<FieldLabel label="Anrede" required={isReq('salutation')} />
							<Select value={fields.salutation} onChange={set('salutation')}>
								<option value="">Auswählen …</option>
								{salutationOptions.map((opt) => (
									<option key={opt} value={opt}>
										{opt}
									</option>
								))}
							</Select>
						</div>
					)}
					<div className="grid grid-cols-2 gap-3">
						{visible.has('firstName') && (
							<div>
								<FieldLabel label="Vorname" required={isReq('firstName')} />
								<TextInput
									value={fields.firstName}
									onChange={set('firstName')}
									placeholder="Max"
									autoFocus
								/>
							</div>
						)}
						{visible.has('lastName') && (
							<div>
								<FieldLabel label="Nachname" required={isReq('lastName')} />
								<TextInput
									value={fields.lastName}
									onChange={set('lastName')}
									placeholder="Mustermann"
								/>
							</div>
						)}
					</div>
					{visible.has('companyRole') && (
						<div className="mt-3">
							<FieldLabel label="Position" required={isReq('companyRole')} />
							<TextInput
								value={fields.companyRole}
								onChange={set('companyRole')}
								placeholder="z. B. Geschäftsführer"
							/>
						</div>
					)}
				</div>

				{(visible.has('emailAddress') || visible.has('phoneNumber')) && (
					<div className="mb-5">
						<SectionLabel icon={BsTelephone}>Kontaktdaten</SectionLabel>
						<div className="grid grid-cols-2 gap-3">
							{visible.has('emailAddress') && (
								<div>
									<FieldLabel label="E-Mail" required={isReq('emailAddress')} />
									<TextInput
										type="email"
										value={fields.emailAddress}
										onChange={set('emailAddress')}
										placeholder="name@firma.de"
									/>
								</div>
							)}
							{visible.has('phoneNumber') && (
								<div>
									<FieldLabel label="Telefon" required={isReq('phoneNumber')} />
									<TextInput
										value={fields.phoneNumber}
										onChange={set('phoneNumber')}
										placeholder="+49 ..."
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{visible.has('linkedinUrl') && (
					<div className="mb-5">
						<SectionLabel icon={BsGlobe}>Social</SectionLabel>
						<div>
							<FieldLabel label="LinkedIn" required={isReq('linkedinUrl')} />
							<TextInput
								value={fields.linkedinUrl}
								onChange={set('linkedinUrl')}
								placeholder="linkedin.com/in/..."
							/>
						</div>
					</div>
				)}

				{/* Custom fields (non-default only) */}
				{hasCustomFields && (
					<div className="mb-5">
						<CustomFieldsSection
							entityType="contact"
							values={customFields}
							inline
							showLabel
							onChange={(fieldId, value) => setCustomFields((prev) => ({ ...prev, [fieldId]: value }))}
						/>
					</div>
				)}

				<Checkbox
					id="new-contact-is-primary"
					checked={isPrimary}
					onChange={setIsPrimary}
					label="Primärkontakt"
				/>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					type="submit"
					variant="solid"
					size="sm"
					loading={submitting}
					disabled={!hasMinInput || hasRequiredFieldsMissing}
				>
					Kontakt hinzufügen
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

NewContactModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	companyId: PropTypes.string.isRequired,
	onCreated: PropTypes.func,
};

export default NewContactModal;
